// ---------------------------------------------------------------------------
// Minimal Map-based event emitter (replaces Vue 2 $nuxt.$on/$off/$emit/$once)
// ---------------------------------------------------------------------------
type Handler = (...args: any[]) => void

interface Emitter {
  on(event: string, handler: Handler): void
  off(event: string, handler: Handler): void
  once(event: string, handler: Handler): void
  emit(event: string, ...args: any[]): void
  /** Remove ALL handlers for every event */
  clear(): void
}

function createEmitter(): Emitter {
  const all = new Map<string, Handler[]>()

  return {
    on(event: string, handler: Handler): void {
      const handlers = all.get(event)
      if (handlers) {
        handlers.push(handler)
      } else {
        all.set(event, [handler])
      }
    },

    off(event: string, handler: Handler): void {
      const handlers = all.get(event)
      if (!handlers) return
      const idx = handlers.indexOf(handler)
      if (idx !== -1) {
        handlers.splice(idx, 1)
      }
      if (handlers.length === 0) {
        all.delete(event)
      }
    },

    once(event: string, handler: Handler): void {
      const wrapper: Handler = (...args) => {
        this.off(event, wrapper)
        handler(...args)
      }
      this.on(event, wrapper)
    },

    emit(event: string, ...args: any[]): void {
      const handlers = all.get(event)
      if (!handlers) return
      // Iterate over a copy so handlers can safely remove themselves
      handlers.slice().forEach(h => h(...args))
    },

    clear(): void {
      all.clear()
    },
  }
}

// ---------------------------------------------------------------------------
// Type definitions
// ---------------------------------------------------------------------------
interface EmissionTracker {
  count: number
  sources: string[]
}

interface ListenerData {
  id: string
  uuid: string
  model: string
  name: string
  callback: Function
  createdAt: number
}

interface ListenerStats {
  total: number
  maxListeners: number
  listeners: Array<{
    id: string
    uuid: string
    model: string
    name: string
    createdAt: number
    age: number
  }>
}

interface EventData {
  data: any
  source: string | null
  timestamp: number
}

interface LegacyListener {
  model: string
  name: string
  uuid: string
  callback: Function
  listenerId?: string | null
}

interface RecursionTracker {
  activeEmissions: Map<string, EmissionTracker>
  maxDepth: number
  canEmit(model: string, id: any, source: string | null): boolean
  trackEmission(model: string, id: any, source: string | null): void
}

interface ListenerTracker {
  maxListeners: number
  activeListeners: Map<string, ListenerData>
  listenerOrder: string[]
  addListener(uuid: string, model: string, name: string, callback: Function): string
  removeListener(listenerId: string): boolean
  removeOldestListener(): void
  removeListenersByUuid(uuid: string): number
  getStats(): ListenerStats
  clear(): void
}

export interface EventBusPlugin {
  listeners: Record<string, LegacyListener[]>
  debug: {
    enabled: boolean
    log(message: string, data?: any): void
    getActiveEmissions(): Map<string, EmissionTracker>
    clearActiveEmissions(): void
    getListenerStats(): ListenerStats
    clearAllListeners(): void
    setMaxListeners(max: number): void
  }
  emit: {
    updated(model: string, data: any, source?: string | null): boolean
    created(model: string, data: any, source?: string | null): boolean
    deleted(model: string, data: any, source?: string | null): boolean
  }
  on: {
    updated(model: string, uuid: string, callback: Function): void
    created(model: string, uuid: string, callback: Function): void
    deleted(model: string, uuid: string, callback: Function): void
    all(model: string, uuid: string, callback: Function): void
  }
  once: {
    updated(model: string, callback: Function): void
    created(model: string, callback: Function): void
    deleted(model: string, callback: Function): void
  }
  off: {
    updated(uuid: string): number
    created(uuid: string): number
    deleted(uuid: string): number
    all(uuid: string): number
  }
  add(name: string, model: string, uuid: string, callback: Function, listenerId?: string | null): void
}


// ---------------------------------------------------------------------------
// Recursion prevention utility
// ---------------------------------------------------------------------------
const recursionTracker: RecursionTracker = {
  activeEmissions: new Map<string, EmissionTracker>(),
  maxDepth: 3,

  canEmit(model: string, id: any, source: string | null): boolean {
    const key = `${model}:${id}`
    const current = this.activeEmissions.get(key) || { count: 0, sources: [] }

    // Check if we're already processing this model/id combination
    if (current.count >= this.maxDepth) {
      console.warn(`Potential recursion detected for ${model}:${id}. Emission blocked.`, {
        count: current.count,
        sources: current.sources,
        newSource: source,
      })
      return false
    }

    // Check if the same source is trying to emit again
    if (source && current.sources.includes(source)) {
      console.warn(`Circular emission detected from source ${source} for ${model}:${id}. Emission blocked.`)
      return false
    }

    return true
  },

  trackEmission(model: string, id: any, source: string | null): void {
    const key = `${model}:${id}`
    const current = this.activeEmissions.get(key) || { count: 0, sources: [] }

    this.activeEmissions.set(key, {
      count: current.count + 1,
      sources: source ? [...current.sources, source] : current.sources,
    })

    // Clean up after a short delay
    setTimeout(() => {
      const updated = this.activeEmissions.get(key)
      if (updated && updated.count > 0) {
        updated.count--
        if (source) {
          updated.sources = updated.sources.filter(s => s !== source)
        }

        if (updated.count === 0) {
          this.activeEmissions.delete(key)
        } else {
          this.activeEmissions.set(key, updated)
        }
      }
    }, 100)
  },
}

// ---------------------------------------------------------------------------
// Plugin
// ---------------------------------------------------------------------------
export default defineNuxtPlugin((_nuxtApp) => {
  // Create the internal emitter that replaces $nuxt.$on/$off/$emit/$once
  const emitter = createEmitter()

  // -----------------------------------------------------------------------
  // Listener tracking utility (scoped to this plugin instance)
  // -----------------------------------------------------------------------
  const listenerTracker: ListenerTracker = {
    maxListeners: 30,
    activeListeners: new Map<string, ListenerData>(),
    listenerOrder: [],

    addListener(uuid: string, model: string, name: string, callback: Function): string {
      const listenerId = `${uuid}_${model}_${name}_${Date.now()}`
      const listenerData: ListenerData = {
        id: listenerId,
        uuid,
        model,
        name,
        callback,
        createdAt: Date.now(),
      }

      // Add to tracking
      this.activeListeners.set(listenerId, listenerData)
      this.listenerOrder.push(listenerId)

      // Check if we exceed the limit
      if (this.listenerOrder.length > this.maxListeners) {
        this.removeOldestListener()
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`[EventBus] Added listener ${listenerId}. Total active: ${this.activeListeners.size}`)
      }

      return listenerId
    },

    removeListener(listenerId: string): boolean {
      const listener = this.activeListeners.get(listenerId)
      if (listener) {
        // Remove from internal emitter
        emitter.off(listener.name, listener.callback as Handler)

        // Remove from tracking
        this.activeListeners.delete(listenerId)
        this.listenerOrder = this.listenerOrder.filter(id => id !== listenerId)

        if (process.env.NODE_ENV === 'development') {
          console.log(`[EventBus] Removed listener ${listenerId}. Total active: ${this.activeListeners.size}`)
        }

        return true
      }
      return false
    },

    removeOldestListener(): void {
      if (this.listenerOrder.length > 0) {
        const oldestId = this.listenerOrder[0]
        const removed = this.removeListener(oldestId)

        if (removed && process.env.NODE_ENV === 'development') {
          console.log(`[EventBus] Removed oldest listener ${oldestId} due to limit (${this.maxListeners})`)
        }
      }
    },

    removeListenersByUuid(uuid: string): number {
      const toRemove: string[] = []

      for (const [listenerId, listener] of this.activeListeners) {
        if (listener.uuid === uuid) {
          toRemove.push(listenerId)
        }
      }

      toRemove.forEach(id => this.removeListener(id))
      return toRemove.length
    },

    getStats(): ListenerStats {
      return {
        total: this.activeListeners.size,
        maxListeners: this.maxListeners,
        listeners: Array.from(this.activeListeners.values()).map((l: ListenerData) => ({
          id: l.id,
          uuid: l.uuid,
          model: l.model,
          name: l.name,
          createdAt: l.createdAt,
          age: Date.now() - l.createdAt,
        })),
      }
    },

    clear(): void {
      // Remove all listeners from internal emitter
      for (const listener of this.activeListeners.values()) {
        emitter.off(listener.name, listener.callback as Handler)
      }

      this.activeListeners.clear()
      this.listenerOrder = []

      if (process.env.NODE_ENV === 'development') {
        console.log('[EventBus] Cleared all tracked listeners')
      }
    },
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  const events: string[] = ['updated', 'created', 'deleted']

  const notification = (model: string, type: string): string => {
    return `${model}:${type}`
  }

  // -----------------------------------------------------------------------
  // Build the public plugin object
  // -----------------------------------------------------------------------
  const plugin: EventBusPlugin = {
    listeners: {},

    // Development helper for debugging
    debug: {
      enabled: process.env.NODE_ENV === 'development',
      log(message: string, data?: any): void {
        if (this.enabled) {
          console.log(`[EventBus] ${message}`, data)
        }
      },
      getActiveEmissions(): Map<string, EmissionTracker> {
        return recursionTracker.activeEmissions
      },
      clearActiveEmissions(): void {
        recursionTracker.activeEmissions.clear()
      },
      getListenerStats(): ListenerStats {
        return listenerTracker.getStats()
      },
      clearAllListeners(): void {
        listenerTracker.clear()
      },
      setMaxListeners(max: number): void {
        listenerTracker.maxListeners = max
      },
    },

    emit: {
      ...Object.fromEntries(
        events.map((event: string) => {
          return [
            event,
            (model: string, data: any, source: string | null = null): boolean => {
              // Extract ID for recursion tracking
              const id = data?.objectID?.()

              // Check if emission should be allowed
              if (!recursionTracker.canEmit(model, id, source)) {
                return false
              }

              // Track this emission
              recursionTracker.trackEmission(model, id, source)

              const name = notification(model, event)
              const eventData: EventData = {
                data,
                source,
                timestamp: Date.now(),
              }
              emitter.emit(name, eventData)
              return true
            },
          ]
        }),
      ),
    } as EventBusPlugin['emit'],

    on: {
      ...Object.fromEntries(
        events.map((event: string) => {
          return [
            event,
            (model: string, uuid: string, callback: Function): void => {
              const name = notification(model, event)

              // Remove existing listener for this uuid+name to prevent duplicates
              const existing = plugin.listeners[uuid]
              if (existing) {
                const dupeIndex = existing.findIndex((l: LegacyListener) => l.name === name)
                if (dupeIndex !== -1) {
                  const dupe = existing[dupeIndex]
                  if (dupe.listenerId) {
                    listenerTracker.removeListener(dupe.listenerId)
                  } else {
                    emitter.off(name, dupe.callback as Handler)
                  }
                  existing.splice(dupeIndex, 1)
                }
              }

              const wrappedCallback = (eventData: EventData | any): void => {
                // Handle both old format (direct data) and new format (wrapped data)
                const data = eventData?.data !== undefined ? eventData.data : eventData
                const source = eventData?.source || null

                if (callback instanceof Function) {
                  callback(data, source, eventData?.timestamp)
                }
              }

              emitter.on(name, wrappedCallback)

              // Track the listener with the new system
              const listenerId = listenerTracker.addListener(uuid, model, name, wrappedCallback)

              // Keep the old system for backward compatibility
              plugin.add(name, model, uuid, wrappedCallback, listenerId)
            },
          ]
        }),
      ),
      all: (model: string, uuid: string, callback: Function): void => {
        events.forEach((event: string) => {
          const name = notification(model, event)

          // Remove existing listener for this uuid+name to prevent duplicates
          const existing = plugin.listeners[uuid]
          if (existing) {
            const dupeIndex = existing.findIndex((l: LegacyListener) => l.name === name)
            if (dupeIndex !== -1) {
              const dupe = existing[dupeIndex]
              if (dupe.listenerId) {
                listenerTracker.removeListener(dupe.listenerId)
              } else {
                emitter.off(name, dupe.callback as Handler)
              }
              existing.splice(dupeIndex, 1)
            }
          }

          const wrappedCallback = (eventData: EventData | any): void => {
            // Handle both old format (direct data) and new format (wrapped data)
            const data = eventData?.data !== undefined ? eventData.data : eventData
            const source = eventData?.source || null

            if (callback instanceof Function) {
              callback(data, source, eventData?.timestamp)
            }
          }

          emitter.on(name, wrappedCallback)

          // Track the listener with the new system
          const listenerId = listenerTracker.addListener(uuid, model, name, wrappedCallback)

          // Keep the old system for backward compatibility
          plugin.add(name, model, uuid, wrappedCallback, listenerId)
        })
      },
    } as EventBusPlugin['on'],

    once: {
      ...Object.fromEntries(
        events.map((event: string) => {
          return [
            event,
            (model: string, callback: Function): void => {
              const name = notification(model, event)
              const wrappedCallback = (eventData: EventData | any): void => {
                // Handle both old format (direct data) and new format (wrapped data)
                const data = eventData?.data !== undefined ? eventData.data : eventData
                const source = eventData?.source || null

                if (callback instanceof Function) {
                  callback(data, source, eventData?.timestamp)
                }
              }

              emitter.once(name, wrappedCallback)

              // Note: once listeners are automatically removed after firing,
              // so we don't need to track them in our system
            },
          ]
        }),
      ),
    } as EventBusPlugin['once'],

    off: {
      ...Object.fromEntries(
        events.map((event: string) => {
          return [
            event,
            (uuid: string): number => {
              // Remove from new tracking system
              const removedCount = listenerTracker.removeListenersByUuid(uuid)

              // Keep old system for backward compatibility
              const listener = plugin.listeners[uuid]

              if (listener) {
                listener.forEach((listener: LegacyListener, index: number) => {
                  const name = notification(listener.model, event)

                  if (name === listener.name) {
                    // Note: listener is already removed by listenerTracker
                    plugin.listeners[uuid]?.splice(index, 1)
                  }
                })
              }

              if (plugin.listeners[uuid]?.length === 0) {
                delete plugin.listeners[uuid]
              }

              return removedCount
            },
          ]
        }),
      ),
      all: (uuid: string): number => {
        // Remove from new tracking system
        const removedCount = listenerTracker.removeListenersByUuid(uuid)

        // Keep old system for backward compatibility
        delete plugin.listeners[uuid]

        return removedCount
      },
    } as EventBusPlugin['off'],

    add(name: string, model: string, uuid: string, callback: Function, listenerId: string | null = null): void {
      if (!plugin.listeners[uuid]) {
        plugin.listeners[uuid] = []
      }

      plugin.listeners[uuid].push({ model, name, uuid, callback, listenerId })
    },
  }

  return { provide: { bus: plugin } }
})
